import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TableHead, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { createDetctionTypeIcon } from "./AlertsUtils/createDetctionTypeIcon";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { trimSelectedUserName } from "./AlertsUtils/trimSelectedUserName";
import { handleCreateAlertTypeNoUnderscore } from "./AlertsTable";
import { logger } from "../../../utils/logger";
import { useAppStore } from "../../../appStore";
import { canSetAlertOutcome } from "./AlertsUtils/alertOutcomePermission";

const CameraInfoTable = ({
  selectedRow,
  setNoteText,
  setNotesDialogOpen,
}: {
  selectedRow: any;
  setNoteText: any;
  setNotesDialogOpen: any;
}) => {
  // ENG-1813: the Notes cell opens the same dialog either way; without the
  // outcome permission it reads as "view", not "edit".
  const { userPermissions } = useAppStore();
  const canEditNotes = canSetAlertOutcome(userPermissions);

  const epochToDateTime = (epoch: number, timezone?: string) => {
    const date = new Date(epoch * 1000);

    // If timezone is provided, convert to that timezone
    if (timezone) {
      try {
        // Use Intl.DateTimeFormat to format the date in the specified timezone
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });

        const parts = formatter.formatToParts(date);
        const month = parts.find((part) => part.type === "month")?.value || "";
        const day = parts.find((part) => part.type === "day")?.value || "";
        const year = parts.find((part) => part.type === "year")?.value || "";
        const hour = parts.find((part) => part.type === "hour")?.value || "";
        const minute =
          parts.find((part) => part.type === "minute")?.value || "";
        const second =
          parts.find((part) => part.type === "second")?.value || "";
        const dayPeriod =
          parts.find((part) => part.type === "dayPeriod")?.value || "";

        return `${month}/${day}/${year}, ${hour}:${minute}:${second} ${dayPeriod}`;
      } catch (error) {
        logger.warn(
          "Invalid timezone provided, falling back to local time:",
          timezone
        );
        // Fall back to original logic if timezone is invalid
      }
    }

    // Original logic for local time or fallback
    let month = (date.getMonth() + 1).toString();
    if (month.length !== 2) {
      month = `0${month}`;
    }
    let day = date.getDate().toString();
    if (day.length !== 2) {
      day = `0${day}`;
    }
    const year = date.getFullYear();
    let hour = date.getHours();
    if (hour >= 12) {
      hour = hour - 12;
    }
    let newHour = hour.toString();
    if (newHour.length !== 2) {
      newHour = `0${newHour}`;
    }
    let minute = date.getMinutes().toString();
    if (minute.length !== 2) {
      minute = `0${minute}`;
    }
    let second = date.getSeconds().toString();
    if (second.length !== 2) {
      second = `0${second}`;
    }
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    return `${month}/${day}/${year}, ${newHour}:${minute}:${second} ${ampm}`;
  };

  const headCellSx = {
    fontSize: "11px",
    fontFamily: "Mulish",
    fontWeight: 700,
    width: "20%",
    py: 0.5,
    px: 1,
  };
  const bodyCellSx = {
    fontSize: "12px",
    fontFamily: "Mulish",
    py: 0.5,
    px: 1,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        // Grows with spare height in the alert detail card (rows stretch).
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        boxShadow: "none",
        border: "1px solid #E5E5E5",
        borderRadius: "4px",
      }}
    >
      <TableContainer sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Table
          size="small"
          sx={{ minWidth: 500, fontFamily: "Mulish", flexGrow: 1 }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={headCellSx}>
                Alert Type
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                Time Stamp
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                Outcome
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                User
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                Date Resolved
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                Notes
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                align="left"
                sx={bodyCellSx}
              >
                {handleCreateAlertTypeNoUnderscore(selectedRow.alert_labels)}
              </TableCell>
              <TableCell
                align="left"
                sx={bodyCellSx}
              >
                {selectedRow.formatted_date}
                &nbsp;
                <Tooltip
                  title={selectedRow.timezone}
                  placement="top"
                  sx={{ fontSize: "12px", fontFamily: "mulish" }}
                >
                  <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                </Tooltip>
              </TableCell>
              <TableCell
                align="left"
                sx={bodyCellSx}
              >
                {selectedRow.detection_type
                  ? createDetctionTypeIcon(
                      selectedRow.detection_type,
                      selectedRow.engineering_outcome?.outcome_v2
                    )
                  : "--"}
              </TableCell>
              <TableCell
                align="left"
                sx={bodyCellSx}
              >
                {selectedRow.user
                  ? trimSelectedUserName(selectedRow.user)
                  : "--"}
              </TableCell>
              <TableCell
                align="left"
                sx={bodyCellSx}
              >
                {selectedRow.updated ? (
                  <div>
                    {epochToDateTime(selectedRow.updated, selectedRow.timezone)}
                    &nbsp;
                    <Tooltip
                      title={selectedRow.timezone}
                      placement="top"
                      sx={{ fontSize: "14px", fontFamily: "mulish" }}
                    >
                      <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                    </Tooltip>
                  </div>
                ) : (
                  "--"
                )}
              </TableCell>
              <TableCell
                align="left"
                sx={bodyCellSx}
              >
                {/* {selectedRow.updated ? ( */}
                <OrangeTextButton
                  text={
                    canEditNotes ? (
                      <EditIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )
                  }
                  onClick={() => {
                    setNoteText(
                      selectedRow.detection_type
                        ? selectedRow.notes
                        : selectedRow.unresolved_notes
                    );
                    setNotesDialogOpen(true);
                  }}
                  disabled={false}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CameraInfoTable;
